/*
==================================================
CATEGORY CAROUSEL

Функция:
главный файл-конструктор карусели категорий.
Хранит состояние, роутинг, вычисляет root categories,
активную категорию, активные подкатегории
и передает всё в mobile / desktop блоки.

Родитель:
store page / category section

Дочерние элементы:
- category-carousel-shell

Использует:
- category-carousel-config

Примечания:
главный файл не содержит тяжелую разметку.
Только логика, состояние и сборка блоков.
==================================================
*/

"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import CategoryCarouselShell from "./category-carousel-shell"

type CategoryItem = {
  id: string
  name: string
  handle?: string | null
  products?: { id: string }[] | null
  category_children?: CategoryItem[] | null
  children?: CategoryItem[] | null
  subcategories?: CategoryItem[] | null
  parent_category_id?: string | null
}

type DisplayCategoryItem = {
  id: string
  name: string
  handle?: string | null
  products?: { id: string }[] | null
  children: DisplayCategoryItem[]
}

type CategoryCarouselProps = {
  categories: CategoryItem[]
  selectedCategory?: string
}

const MOBILE_CLOSE_AFTER_SELECT_DELAY_MS = 200

const getCategoryChildren = (category: CategoryItem): CategoryItem[] => {
  if (
    Array.isArray(category.category_children) &&
    category.category_children.length
  ) {
    return category.category_children
  }

  if (Array.isArray(category.children) && category.children.length) {
    return category.children
  }

  if (Array.isArray(category.subcategories) && category.subcategories.length) {
    return category.subcategories
  }

  return []
}

const normalizeCategory = (category: CategoryItem): DisplayCategoryItem => {
  const rawChildren = getCategoryChildren(category)

  return {
    id: category.id,
    name: category.name,
    handle: category.handle,
    products: category.products,
    children: rawChildren
      .filter((child) => !!child?.handle)
      .map((child) => normalizeCategory(child)),
  }
}

const findRootCategories = (categories: CategoryItem[]): CategoryItem[] => {
  const categoryIds = new Set(categories.map((cat) => cat.id))

  return categories.filter((cat) => {
    if (!cat?.handle) return false

    if (cat.parent_category_id) return false

    const belongsAsNestedChild = categories.some((candidate) => {
      const nested = getCategoryChildren(candidate)
      return nested.some((child) => child.id === cat.id)
    })

    if (belongsAsNestedChild) return false

    if (cat.parent_category_id && categoryIds.has(cat.parent_category_id)) {
      return false
    }

    return true
  })
}

export const getCountValue = (
  category?: DisplayCategoryItem | null
): number => {
  if (!category) return 0

  const ownCount = Array.isArray(category.products)
    ? category.products.length
    : 0

  if (!category.children.length) {
    return ownCount
  }

  return category.children.reduce((sum, child) => {
    return sum + getCountValue(child)
  }, ownCount)
}

export default function CategoryCarousel({
  categories,
  selectedCategory,
}: CategoryCarouselProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileCompact, setMobileCompact] = useState(false)
  const [expandedCategoryHandle, setExpandedCategoryHandle] = useState<
    string | null
  >(null)

  const mobileRootRef = useRef<HTMLDivElement | null>(null)
  const mobileScrollRef = useRef<HTMLDivElement | null>(null)
  const mobileCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const desktopScrollRef = useRef<HTMLDivElement | null>(null)
  const lastWindowY = useRef(0)

  const rootItems = useMemo<DisplayCategoryItem[]>(() => {
    const normalized = findRootCategories(categories).map((cat) =>
      normalizeCategory(cat)
    )

    return [
      {
        id: "all-products",
        name: "All products",
        handle: "",
        products: null,
        children: [],
      },
      ...normalized,
    ]
  }, [categories])

  const getConfirmedExpandedHandle = (
    currentSelectedCategory?: string
  ): string | null => {
    if (!currentSelectedCategory) {
      return null
    }

    const matchedRoot = rootItems.find((item) => {
      if (item.handle === currentSelectedCategory) return true

      return item.children.some(
        (child) => child.handle === currentSelectedCategory
      )
    })

    if (!matchedRoot?.children.length) {
      return null
    }

    return matchedRoot.handle || null
  }

  const getParentHandleForSubcategory = (
    handle?: string | null
  ): string | null => {
    if (!handle) return null

    const matchedRoot = rootItems.find((item) =>
      item.children.some((child) => child.handle === handle)
    )

    return matchedRoot?.handle || null
  }

  useEffect(() => {
    return () => {
      if (mobileCloseTimerRef.current) {
        clearTimeout(mobileCloseTimerRef.current)
      }
    }
  }, [])

  const visibleSubcategories = useMemo(() => {
    if (!expandedCategoryHandle) return []

    const matchedRoot = rootItems.find(
      (item) => item.handle === expandedCategoryHandle
    )

    return matchedRoot?.children || []
  }, [rootItems, expandedCategoryHandle])

  const activeMobileIndex = useMemo(() => {
    return rootItems.findIndex((item) => {
      if (item.handle === "") return !selectedCategory
      if (item.handle === selectedCategory) return true

      return item.children.some((child) => child.handle === selectedCategory)
    })
  }, [rootItems, selectedCategory])

  const activeDesktopIndex = activeMobileIndex

  const isMobileViewport = () => {
    if (typeof window === "undefined") return false

    return window.matchMedia("(max-width: 767px)").matches
  }

  const closeMobilePanelsAfterSelect = () => {
    if (!isMobileViewport()) return

    const shouldCompactAfterClose = window.scrollY > 20

    // Важно:
    // - выбранную категорию и раскрытую родительскую категорию не очищаем;
    // - задержка нужна только для mobile, чтобы пользователь успел увидеть подсветку выбранного пункта;
    // - если на момент нажатия мы были наверху страницы, закрываемся до основной панели;
    // - если на момент нажатия были в середине списка товаров, закрываемся до трея.
    if (mobileCloseTimerRef.current) {
      clearTimeout(mobileCloseTimerRef.current)
    }

    mobileCloseTimerRef.current = setTimeout(() => {
      setMobileOpen(false)
      setMobileCompact(shouldCompactAfterClose)
      mobileCloseTimerRef.current = null
    }, MOBILE_CLOSE_AFTER_SELECT_DELAY_MS)
  }

  const getExpandedHandleForSelectedHandle = (
    handle?: string | null
  ): string | null => {
    if (!handle) return null

    const parentHandle = getParentHandleForSubcategory(handle)
    if (parentHandle) return parentHandle

    const matchedRoot = rootItems.find((item) => item.handle === handle)
    if (matchedRoot?.children.length) return matchedRoot.handle || null

    return null
  }

  useEffect(() => {
    setExpandedCategoryHandle(getConfirmedExpandedHandle(selectedCategory))
  }, [rootItems, selectedCategory])

  useEffect(() => {
    if (mobileOpen) return

    setExpandedCategoryHandle(getConfirmedExpandedHandle(selectedCategory))
  }, [mobileOpen, rootItems, selectedCategory])

  const applyCategory = (
    handle?: string | null,
    options?: { closeMobile?: boolean }
  ) => {
    const params = new URLSearchParams(searchParams.toString())

    if (handle) {
      params.set("category", handle)
      params.delete("page")
    } else {
      params.delete("category")
      params.delete("page")
    }

    const query = params.toString()

    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    })

    if (options?.closeMobile ?? true) {
      closeMobilePanelsAfterSelect()
    }
  }

  const handleDesktopSubcategorySelect = (handle?: string | null) => {
    setExpandedCategoryHandle(getParentHandleForSubcategory(handle))
    applyCategory(handle)
  }

  const handleMobileSubcategorySelect = (handle?: string | null) => {
    setExpandedCategoryHandle(getExpandedHandleForSelectedHandle(handle))
    applyCategory(handle, { closeMobile: true })
  }

  const handleRootCategoryClick = (item: DisplayCategoryItem) => {
    if (item.handle === "") {
      setExpandedCategoryHandle(null)
      applyCategory("", { closeMobile: true })
      return
    }

    if (item.children.length) {
      setExpandedCategoryHandle((prev) =>
        prev === item.handle ? null : item.handle || null
      )
      return
    }

    setExpandedCategoryHandle(null)
    applyCategory(item.handle, { closeMobile: true })
  }

  const handleDesktopRootHover = (_item: DisplayCategoryItem) => {
    return
  }

  const scrollDesktopByAmount = (direction: "left" | "right") => {
    const node = desktopScrollRef.current
    if (!node) return

    const amount = Math.round(node.clientWidth * 0.72)

    node.scrollBy({
      left: direction === "right" ? amount : -amount,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    if (!mobileOpen) return

    lastWindowY.current = window.scrollY

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target) return

      if (mobileRootRef.current?.contains(target)) return
      setMobileOpen(false)
    }

    const handleWindowScroll = () => {
      const currentY = window.scrollY
      const diff = Math.abs(currentY - lastWindowY.current)

      if (diff > 6) {
        setMobileOpen(false)
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)
    document.addEventListener("touchstart", handleOutsideClick, {
      passive: true,
    })
    window.addEventListener("scroll", handleWindowScroll, { passive: true })

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      document.removeEventListener("touchstart", handleOutsideClick)
      window.removeEventListener("scroll", handleWindowScroll)
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!mobileOpen) return

    const node = mobileScrollRef.current
    if (!node) return

    const activeCard = node.querySelector<HTMLElement>(
      '[data-active-mobile-category="true"]'
    )

    if (!activeCard) return

    const leftOffset = Math.max(activeCard.offsetLeft - 16, 0)

    node.scrollTo({
      left: leftOffset,
      behavior: "auto",
    })
  }, [mobileOpen, activeMobileIndex])

  useEffect(() => {
    const node = desktopScrollRef.current
    if (!node) return

    const activeCard = node.querySelector<HTMLElement>(
      '[data-active-desktop-category="true"]'
    )

    if (!activeCard) return

    const cardLeft = activeCard.offsetLeft
    const cardWidth = activeCard.offsetWidth
    const viewportWidth = node.clientWidth

    const leftOffset = Math.max(
      cardLeft - Math.max((viewportWidth - cardWidth) / 2, 24),
      0
    )

    node.scrollTo({
      left: leftOffset,
      behavior: "auto",
    })
  }, [activeDesktopIndex])

  useEffect(() => {
    const handleCompactOnScroll = () => {
      const shouldCompact = window.scrollY > 20
      setMobileCompact(shouldCompact)
    }

    handleCompactOnScroll()

    window.addEventListener("scroll", handleCompactOnScroll, {
      passive: true,
    })

    return () => {
      window.removeEventListener("scroll", handleCompactOnScroll)
    }
  }, [])

  return (
    <CategoryCarouselShell
      rootItems={rootItems}
      selectedCategory={selectedCategory}
      mobileOpen={mobileOpen}
      setMobileOpen={setMobileOpen}
      mobileCompact={mobileCompact}
      expandedCategoryHandle={expandedCategoryHandle}
      setExpandedCategoryHandle={setExpandedCategoryHandle}
      visibleSubcategories={visibleSubcategories}
      mobileRootRef={mobileRootRef}
      mobileScrollRef={mobileScrollRef}
      desktopScrollRef={desktopScrollRef}
      onApplyCategory={applyCategory}
      onDesktopSubcategorySelect={handleDesktopSubcategorySelect}
      onMobileSubcategorySelect={handleMobileSubcategorySelect}
      onRootCategoryClick={handleRootCategoryClick}
      onDesktopRootHover={handleDesktopRootHover}
      onDesktopScroll={scrollDesktopByAmount}
    />
  )
}
